import { LitElement, html, css } from 'lit';
import './components/FlashCard.js';
import './components/QuizMode.js';
import './components/GameMode.js';
import './components/ConjugationsMode.js';
import './components/SettingsMode.js';
import './components/ProgressTracker.js';
import './components/ModeSelector.js';
import { getVocabularyList, getRandomWords } from './data/words.js';

class VocabApp extends LitElement {
  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
    }

    .app-container {
      max-width: 480px;
      margin: 0 auto;
      padding: 20px;
      min-height: 100vh;
      box-sizing: border-box;
    }

    .header {
      text-align: center;
      margin-bottom: 24px;
    }

    .mode-selector-wrapper {
      margin-bottom: 24px;
      position: relative;
    }

    .settings-btn {
      position: fixed;
      top: 16px;
      right: 16px;
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: white;
      border: none;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      z-index: 100;
    }

    .settings-btn:hover {
      transform: scale(1.1) rotate(30deg);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    }

    .settings-btn svg {
      width: 22px;
      height: 22px;
      stroke: #667eea;
      fill: none;
      stroke-width: 2;
    }

    .settings-btn.active {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }

    .settings-btn.active svg {
      stroke: white;
    }

    .content-area {
      margin-bottom: 24px;
    }

    .card-nav {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 20px;
      gap: 12px;
    }

    .nav-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 14px 24px;
      background: white;
      border: none;
      border-radius: 12px;
      font-size: 0.95rem;
      font-weight: 600;
      color: #667eea;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      flex: 1;
    }

    .nav-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
    }

    .nav-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }

    .nav-btn svg {
      width: 20px;
      height: 20px;
    }

    .card-counter {
      font-size: 0.875rem;
      color: #666;
      font-weight: 500;
      padding: 0 12px;
    }

    .shuffle-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
      padding: 14px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      border-radius: 12px;
      font-size: 0.95rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      margin-top: 16px;
    }

    .shuffle-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
    }

    .shuffle-btn svg {
      width: 20px;
      height: 20px;
    }

    .footer {
      text-align: center;
      padding: 20px 0;
      color: #888;
      font-size: 0.8rem;
    }

    .toast {
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%) translateY(100px);
      background: #333;
      color: white;
      padding: 12px 24px;
      border-radius: 8px;
      font-size: 0.9rem;
      opacity: 0;
      transition: all 0.3s ease;
      z-index: 1000;
    }

    .toast.show {
      transform: translateX(-50%) translateY(0);
      opacity: 1;
    }

    .loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 300px;
      gap: 16px;
    }

    .loading-spinner {
      width: 48px;
      height: 48px;
      border: 4px solid rgba(102, 126, 234, 0.2);
      border-top-color: #667eea;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .loading-text {
      color: #666;
      font-size: 1rem;
    }

    @media (max-width: 480px) {
      .app-container {
        padding: 16px;
      }

      .nav-btn {
        padding: 12px 16px;
        font-size: 0.9rem;
      }
    }
  `;

  static properties = {
    currentMode: { type: String },
    currentCardIndex: { type: Number },
    words: { type: Array },
    allWords: { type: Array },
    progress: { type: Object },
    toastMessage: { type: String },
    showToast: { type: Boolean },
    isLoading: { type: Boolean },
    conjugationsRefreshKey: { type: Number }
  };

  constructor() {
    super();
    this.currentMode = 'flashcard';
    this.currentCardIndex = 0;
    this.words = [];
    this.allWords = [];
    this.progress = this._loadProgress();
    this.toastMessage = '';
    this.showToast = false;
    this.isLoading = true;
    this.conjugationsRefreshKey = 0;
    
    // Load vocabulary asynchronously
    this._loadVocabulary();
  }

  async _loadVocabulary() {
    console.log('Starting vocabulary load...');
    try {
      // Add timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Laadtijd verstreken')), 10000)
      );
      
      console.log('Fetching vocabulary list...');
      this.allWords = await Promise.race([getVocabularyList(), timeoutPromise]);
      console.log(`Loaded ${this.allWords.length} words`);
      
      this.words = await getRandomWords(10);
      console.log(`Selected ${this.words.length} random words`);
      
      if (this.allWords.length === 0) {
        this.toastMessage = 'Geen woorden geladen. Controleer je instellingen.';
        this.showToast = true;
      }
    } catch (error) {
      console.error('Failed to load vocabulary:', error);
      this.toastMessage = 'Fout bij laden: ' + error.message;
      this.showToast = true;
    }
    console.log('Setting isLoading to false');
    this.isLoading = false;
  }

  _loadProgress() {
    const saved = localStorage.getItem('vocabProgress');
    if (saved) {
      return JSON.parse(saved);
    }
    return {
      mastered: [],
      learning: [],
      attempts: 0,
      correct: 0
    };
  }

  _saveProgress() {
    localStorage.setItem('vocabProgress', JSON.stringify(this.progress));
  }

  _showToast(message) {
    this.toastMessage = message;
    this.showToast = true;
    setTimeout(() => {
      this.showToast = false;
    }, 2000);
  }

  _handleModeChange(e) {
    this.currentMode = e.detail.mode;
    this.currentCardIndex = 0;
  }

  _nextCard() {
    if (this.currentCardIndex < this.words.length - 1) {
      this.currentCardIndex++;
    }
  }

  _prevCard() {
    if (this.currentCardIndex > 0) {
      this.currentCardIndex--;
    }
  }

  async _shuffleWords() {
    this.words = await getRandomWords(10);
    this.currentCardIndex = 0;
    this._showToast('Nieuwe woorden gemengd!');
  }

  _handleAnswer(e) {
    const { correct, word } = e.detail;
    this.progress.attempts++;
    
    if (correct) {
      this.progress.correct++;
      if (!this.progress.mastered.includes(word.id)) {
        this.progress.mastered.push(word.id);
        this.progress.learning = this.progress.learning.filter(id => id !== word.id);
      }
    } else {
      if (!this.progress.learning.includes(word.id) && !this.progress.mastered.includes(word.id)) {
        this.progress.learning.push(word.id);
      }
    }
    
    this._saveProgress();
  }

  _handleResetProgress() {
    this.progress = {
      mastered: [],
      learning: [],
      attempts: 0,
      correct: 0
    };
    this._saveProgress();
    this._showToast('Voortgang gereset!');
  }

  _renderLoading() {
    return html`
      <div class="loading">
        <div class="loading-spinner"></div>
        <p class="loading-text">Woordenlijst laden...</p>
      </div>
    `;
  }

  _renderFlashcardMode() {
    if (this.words.length === 0) {
      return html`<p style="text-align: center; color: #666;">Geen woorden beschikbaar. Controleer je woordenlijst.</p>`;
    }
    return html`
      <flash-card 
        .word="${this.words[this.currentCardIndex]}"
        @card-flip="${() => {}}"
        @swipe-left="${this._nextCard}"
        @swipe-right="${this._prevCard}"
      ></flash-card>
      <div class="card-nav">
        <button 
          class="nav-btn" 
          @click="${this._prevCard}"
          ?disabled="${this.currentCardIndex === 0}"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          Vorige
        </button>
        <span class="card-counter">${this.currentCardIndex + 1} / ${this.words.length}</span>
        <button 
          class="nav-btn" 
          @click="${this._nextCard}"
          ?disabled="${this.currentCardIndex === this.words.length - 1}"
        >
          Volgende
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
      <button class="shuffle-btn" @click="${this._shuffleWords}">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="16 3 21 3 21 8"></polyline>
          <line x1="4" y1="20" x2="21" y2="3"></line>
          <polyline points="21 16 21 21 16 21"></polyline>
          <line x1="15" y1="15" x2="21" y2="21"></line>
          <line x1="4" y1="4" x2="9" y2="9"></line>
        </svg>
        Nieuwe Woorden Mengen
      </button>
    `;
  }

  _renderQuizMode() {
    return html`
      <quiz-mode 
        .words="${this.words}"
        @answer="${this._handleAnswer}"
        @restart="${async () => this.words = await getRandomWords(10)}"
      ></quiz-mode>
    `;
  }

  _renderGameMode() {
    return html`
      <game-mode 
        .allWords="${this.allWords}"
        @restart="${() => {}}"
      ></game-mode>
    `;
  }

  _renderConjugationsMode() {
    return html`
      <conjugations-mode .refreshKey="${this.conjugationsRefreshKey}"></conjugations-mode>
    `;
  }

  _renderSettingsMode() {
    return html`
      <settings-mode 
        @settings-saved="${async () => {
          // Reload vocabulary with new settings
          const { reloadVocabulary } = await import('./data/words.js');
          await reloadVocabulary();
          // Reload current words
          const { getRandomWords } = await import('./data/words.js');
          this.words = await getRandomWords(10);
          this.allWords = await import('./data/words.js').then(m => m.vocabularyList);
          // Force conjugations mode to refresh
          this.conjugationsRefreshKey++;
          this._showToast('Instellingen opgeslagen! Woordenlijst bijgewerkt.');
        }}"
      ></settings-mode>
    `;
  }

  _renderProgressMode() {
    return html`
      <progress-tracker 
        .progress="${this.progress}"
        .words="${this.allWords}"
        @reset-progress="${this._handleResetProgress}"
      ></progress-tracker>
    `;
  }

  render() {
    if (this.isLoading) {
      return html`
        <div class="app-container">
          ${this._renderLoading()}
        </div>
      `;
    }

    let content;
    switch (this.currentMode) {
      case 'flashcard':
        content = this._renderFlashcardMode();
        break;
      case 'quiz':
        content = this._renderQuizMode();
        break;
      case 'game':
        content = this._renderGameMode();
        break;
      case 'conjugations':
        content = this._renderConjugationsMode();
        break;
      case 'settings':
        content = this._renderSettingsMode();
        break;
      case 'progress':
        content = this._renderProgressMode();
        break;
      default:
        content = this._renderFlashcardMode();
    }

    const isSettingsMode = this.currentMode === 'settings';

    return html`
      <div class="app-container">
        <button 
          class="settings-btn ${isSettingsMode ? 'active' : ''}"
          @click="${() => this._handleModeChange({ detail: { mode: isSettingsMode ? 'flashcard' : 'settings' } })}"
          title="${isSettingsMode ? 'Instellingen Sluiten' : 'Instellingen'}"
        >
          <svg viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
        </button>

        <div class="mode-selector-wrapper">
          <mode-selector 
            .activeMode="${this.currentMode}"
            @mode-change="${this._handleModeChange}"
          ></mode-selector>
        </div>

        <div class="content-area">
          ${content}
        </div>
      </div>

      <div class="toast ${this.showToast ? 'show' : ''}">
        ${this.toastMessage}
      </div>
    `;
  }
}

customElements.define('vocab-app', VocabApp);
